import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/utils/community";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
    setMobileCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const transparent = isHome && !scrolled && !mobileOpen;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  const navLinkClass = (active: boolean) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? "text-primary-700"
        : transparent
        ? "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"
        : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md shadow-[0_1px_0_0_oklch(var(--background-200))]"
      }`}
    >
      <nav className="w-full px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-lg brand-gradient flex items-center justify-center text-background-50">
            <i className="ri-chat-smile-3-fill text-lg" />
          </span>
          <span
            className={`font-logo text-xl leading-none ${
              transparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            香蕉社区
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1">
          <Link to="/" className={navLinkClass(location.pathname === "/")}>
            首页
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((v) => !v)}
              className={`${navLinkClass(location.pathname.startsWith("/category"))} flex items-center gap-1 whitespace-nowrap cursor-pointer`}
            >
              话题分类
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-full mt-2 w-[420px] p-3 rounded-xl bg-background-50 border border-background-200 animate-fade-in">
                <div className="grid grid-cols-2 gap-1">
                  {allCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors group"
                    >
                      <span className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} text-lg`} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-950 group-hover:text-primary-700">
                          {cat.name}
                        </span>
                        <span className="block text-xs text-foreground-500 truncate">
                          {cat.topicCount} 个话题
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link to="/about" className={navLinkClass(location.pathname === "/about")}>
            关于我们
          </Link>
          <Link to="/contact" className={navLinkClass(location.pathname === "/contact")}>
            联系我们
          </Link>
        </div>

        {/* Search + Post + mobile toggle */}
        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            className={`hidden lg:flex items-center gap-2 h-10 px-3 rounded-full border transition-colors w-64 ${
              transparent
                ? "bg-background-50/15 border-background-50/30"
                : "bg-background-100 border-background-200"
            }`}
          >
            <i className={`ri-search-line text-sm ${transparent ? "text-background-50/80" : "text-foreground-400"}`} />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索话题、标签或内容"
              className={`flex-1 bg-transparent outline-none text-sm ${
                transparent
                  ? "text-background-50 placeholder:text-background-50/70"
                  : "text-foreground-900 placeholder:text-foreground-400"
              }`}
            />
          </form>

          <Link
            to="/post"
            className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-base" />
            发布
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="打开菜单"
            className={`md:hidden w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer ${
              transparent ? "text-background-50" : "text-foreground-900"
            }`}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto animate-fade-in">
          <div className="px-4 py-4 space-y-3">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 h-11 px-3 rounded-full bg-background-100 border border-background-200"
            >
              <i className="ri-search-line text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索话题、标签或内容"
                className="flex-1 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
              />
              <button type="submit" className="text-primary-700 text-sm font-medium whitespace-nowrap cursor-pointer">
                搜索
              </button>
            </form>

            <div className="flex flex-col">
              <Link to="/" className="py-3 px-2 text-sm font-medium text-foreground-900 border-b border-background-100">
                首页
              </Link>
              <button
                type="button"
                onClick={() => setMobileCategoryOpen((v) => !v)}
                className="py-3 px-2 text-sm font-medium text-foreground-900 border-b border-background-100 flex items-center justify-between cursor-pointer"
              >
                话题分类
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCategoryOpen ? "rotate-180" : ""}`} />
              </button>
              {mobileCategoryOpen && (
                <div className="grid grid-cols-2 gap-2 py-3">
                  {allCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-2 p-2 rounded-lg bg-background-100 text-sm text-foreground-800"
                    >
                      <i className={`${cat.icon} text-primary-600`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
              <Link to="/about" className="py-3 px-2 text-sm font-medium text-foreground-900 border-b border-background-100">
                关于我们
              </Link>
              <Link to="/contact" className="py-3 px-2 text-sm font-medium text-foreground-900">
                联系我们
              </Link>
            </div>

            <Link
              to="/post"
              className="flex items-center justify-center gap-1.5 h-11 rounded-full bg-primary-500 text-foreground-950 text-sm font-medium"
            >
              <i className="ri-add-line" />
              发布内容
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}